import { ReactNode, useEffect } from 'react';
import { DropzoneState, FileError, FileRejection, useDropzone } from 'react-dropzone';

import { useToastActions } from 'stores/toastStore';

const ALLOWED_TYPES: { [key: string]: string[] } = {
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/pdf': ['.pdf'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/*': ['.jpeg', '.jpg', '.png', '.tiff'],
};

// LlamaParse imposes a hard 512 MB per-file limit; block oversize files before upload.
const MAX_FILE_SIZE_BYTES = 512 * 1024 * 1024;

const formatFileSize = (bytes: number) => {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

type FileDropZoneInput = {
  children: (state: DropzoneState) => ReactNode;
  onFilesSelected: (acceptedFiles: File[], fileRejections: FileRejection[]) => void;

  allowedTypes?: { [key: string]: string[] };
  currentFileCount?: number;
  disabled?: boolean;
  maxFiles?: number;
  noClick?: boolean;
  noDrag?: boolean;
  notifyRejections?: boolean;
};

export default function FileDropZone(props: FileDropZoneInput) {
  const {
    allowedTypes = ALLOWED_TYPES,
    children,
    currentFileCount = 0,
    disabled = false,
    maxFiles = 10,
    noClick = false,
    noDrag = false,
    notifyRejections = true,
    onFilesSelected,
  } = props;

  const { addToast } = useToastActions();

  const remainingAllowedFilesCount = maxFiles - currentFileCount;

  const quantityValidator: (file: File) => FileError | null = (file: File) => {
    if (currentFileCount >= maxFiles) {
      return {
        code: 'too-many-files',
        message: 'Too many files',
        file: file,
      };
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return {
        code: 'file-too-large',
        message: 'File exceeds 512 MB',
        file: file,
      };
    }
    return null;
  };

  const dropzoneProps = useDropzone({
    onDrop: onFilesSelected,
    accept: allowedTypes,
    maxFiles: remainingAllowedFilesCount,
    noClick,
    noDrag,
    disabled,
    validator: quantityValidator,
    // showOpenFilePicker mishandles some MIME/extension pairs (e.g. CSV files
    // are un-selectable while drag-and-drop accepts them). The classic input
    // dialog honors the same `accept` list as the drop validation.
    useFsAccessApi: false,
  });

  useEffect(() => {
    if (notifyRejections && dropzoneProps.fileRejections.length) {
      let tooManyFilesNotified = false;

      dropzoneProps.fileRejections.forEach((rejection) => {
        rejection.errors.forEach((err) => {
          let text = '';
          switch (err.code) {
            case 'too-many-files':
              if (!tooManyFilesNotified) {
                tooManyFilesNotified = true;
                text = `A maximum of ${maxFiles} files can be uploaded at a time.`;
                addToast({ text });
              }
              break;
            case 'file-invalid-type':
              text = `${rejection.file.name} is not valid. File must be one of the following types; ${Object.keys(
                allowedTypes
              ).join(', ')}`;
              addToast({ text });
              break;
            case 'file-too-large':
              text = `${rejection.file.name} is ${formatFileSize(
                rejection.file.size
              )}, which exceeds the 512 MB upload limit.`;
              addToast({ text });
              break;
            default:
          }
        });
      });
    }
  }, [notifyRejections, dropzoneProps.fileRejections, maxFiles, allowedTypes, addToast]);

  return <>{children(dropzoneProps)}</>;
}
